import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  OnDestroy
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { _HttpClient } from '@delon/theme';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { InlineAddPanelComponent } from '../../inline-add-panel/inline-add-panel.component';
import { StockAddConfigService } from '../../inline-add-panel/stock-add-config.service';
import { SelectInstantBaseNewComponent } from '../selectInstant-base-new';

@Component({
  selector: 'app-stock-select',
  templateUrl: './stock-select.component.html',
  styleUrls: ['./stock-select.component.less'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StockSelectComponent),
      multi: true
    }
  ]
})
export class StockSelectComponent
  extends SelectInstantBaseNewComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;

  stockConfig: any;
  inlineDefaults: any = null;
  private overlayRef: OverlayRef | null = null;
  private destroy$ = new Subject<void>();
  private currentInlineAddPanel: InlineAddPanelComponent | null = null;
  private visibilityObserver?: IntersectionObserver;
  private restoreInlineOnVisible: boolean = false;
  private restoreInlineData: any = null;

  constructor(
    http: _HttpClient,
    router: Router,
    cdr: ChangeDetectorRef,
    private reuseTabService: ReuseTabService,
    private stockAddConfigService: StockAddConfigService,
    private overlay: Overlay,
    private elementRef: ElementRef
  ) {
    super(http, cdr, router);
  }

  @Input() selectDisabled: boolean = false;

  @Input() showStockControlOnly: boolean = false;
  @Input() showSubItemOnly: boolean = false;
  @Input() displayEInvReq = false;

  @Input() labelField: 'code' | 'name' | null = null;

  @Output() readonly addNewStock = new EventEmitter<any>();

  NotShowInReport: boolean = false;

  // Prevent dropdown being cut off near viewport bottom (e.g. Stock Bundle table)
  dropdownPlacement: 'bottomLeft' | 'topLeft' = 'bottomLeft';

  private updateDropdownPlacementFromViewport(): void {
    try {
      const hostEl = this.nzSelectComponent?.originElement
        ?.nativeElement as HTMLElement | undefined;
      const rect = hostEl?.getBoundingClientRect();
      if (!rect) return;

      // Rough desired height: 4 options * 60px + padding
      const desiredDropdownHeight = 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      this.dropdownPlacement =
        spaceBelow < desiredDropdownHeight && spaceAbove > spaceBelow
          ? 'topLeft'
          : 'bottomLeft';
    } catch {}
  }

  ngOnInit(): void {
    this.checkAddNewOptionInReportAccess();
    this.stockConfig = this.stockAddConfigService.getStockConfig();
    // Listen to search form changes to store instant data
    this.searchForm.controls.keyword.valueChanges.subscribe(value => {
      if (value) {
        const trimmedValue = value.trim();
        this.storedInstantData = trimmedValue;
        // Also store in pendingInstantData for Add New (handled by base class)
        this.pendingInstantData = trimmedValue;
      }
    });
    // Ensure the correct item is active whenever the list updates while open
    this.onListFilled.subscribe(() => {
      if ((this.nzSelectComponent as any)?.nzOpen) {
        this.ensureFirstItemActivatedAndFocused();
      }
    });
  }

  ngAfterViewInit(): void {
    // Observe component visibility (e.g., when switching tabs) and close overlay when hidden
    try {
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(
          entries => {
            for (const entry of entries) {
              if (!entry.isIntersecting) {
                if (this.overlayRef) {
                  // Capture current form state to restore when tab becomes visible again
                  try {
                    this.restoreInlineData =
                      this.currentInlineAddPanel?.formGroup?.value || null;
                  } catch {
                    this.restoreInlineData = null;
                  }
                  this.restoreInlineOnVisible = true;
                  this.closeOverlay();
                }
              } else if (entry.isIntersecting) {
                if (this.restoreInlineOnVisible) {
                  const dataToRestore = this.restoreInlineData;
                  this.restoreInlineOnVisible = false;
                  this.restoreInlineData = null;
                  // Re-open overlay and restore data
                  this.openOverlay(dataToRestore);
                }
              }
            }
          },
          { root: null, threshold: 0 }
        );
        this.visibilityObserver.observe(this.elementRef.nativeElement);
      }
    } catch {}

    const nzSelectElement =
      this.nzSelectComponent?.originElement?.nativeElement;
    if (nzSelectElement) {
      const searchInput = nzSelectElement.querySelector('input');
      if (searchInput) {
        searchInput.addEventListener('input', (event: any) => {
          if (
            event?.target?.value !== undefined &&
            event?.target?.value !== null
          ) {
            const trimmedValue = String(event.target.value).trim();
            this.storedInstantData = trimmedValue;
            // Also store in pendingInstantData for Add New (handled by base class)
            this.pendingInstantData = trimmedValue;
          }
        });
      }
    }

    // No longer need mutation observer - using simpler approach
  }

  override onDropdownOpenChange(open: boolean): void {
    if (open) this.updateDropdownPlacementFromViewport();
    super.onDropdownOpenChange(open);
  }

  override handleClick(event: MouseEvent): void {
    // Placement must be set BEFORE the overlay opens; openChange can be too late.
    this.updateDropdownPlacementFromViewport();
    super.handleClick(event);
  }

  override handleKeyDown(event: KeyboardEvent): void {
    // Same for keyboard opens (Space / Enter can open the dropdown).
    this.updateDropdownPlacementFromViewport();
    super.handleKeyDown(event);
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeOverlay();
  }

  getLabel(item: any) {
    if (item) {
      if (this.labelField === 'code') return item.code;
      else if (this.labelField === 'name') return item.name;
      return `${item.code} - ${item.name}`;
    }
    return '';
  }

  getUrl(searchValue: string): string {
    let filter = `IsActive eq true`;
    if (this.showStockControlOnly === true) {
      filter += ` and IsStockControl eq true`;
    }
    if (this.showSubItemOnly === true) {
      filter += ` and IsBundled eq false`;
    }

    if (searchValue)
      return `api/stocks/lookup?$count=false&$top=50&$skip=0&$filter=${filter}&keyword=${encodeURIComponent(searchValue)}`;
    else
      return `api/stocks/lookup?$count=false&$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
  }

  // set the location want to navigate
  jump(): string {
    return '/stk/stock/detail/0';
  }

  // set the param
  queryParam(): String {
    return 'StockId';
  }

  checkAddNewOptionInReportAccess(): boolean {
    let curUrl = this.reuseTabService.curUrl.toLowerCase();

    if (curUrl.includes('report') || curUrl.includes('einv/')) {
      this.NotShowInReport = true;
    } else {
      this.NotShowInReport = false;
    }

    return this.NotShowInReport;
  }

  // Override the addNew method to show inline panel instead of navigation
  override addNew(): void {
    // Use pendingInstantData which was captured synchronously in base class onAddNewClick
    // This ensures we have the data even if storedInstantData was cleared
    const instantDataToUse = this.pendingInstantData || this.storedInstantData;

    // If we still don't have data, try to capture it
    if (!instantDataToUse) {
      this.captureCurrentSearchValue();
      const currentSearchValue = this.getCurrentSearchValue();
      if (currentSearchValue) {
        this.pendingInstantData = currentSearchValue;
      }
    }

    // Final fallback - use whatever we have
    const finalInstantData =
      this.pendingInstantData || this.storedInstantData || '';

    // Load defaults first, then show panel
    this.fetchInlineDefaults().then(
      () => {
        this.showInlineAddPanel = true;
        // Close the nz-select dropdown explicitly and blur to avoid it reclaiming focus
        if (this.nzSelectComponent) {
          try {
            this.nzSelectComponent.setOpenState(false);
          } catch {}
          try {
            (this.nzSelectComponent as any).blur();
          } catch {}
        }
        this.openOverlay();
      }
    );
  }

  private isSmallScreen(): boolean {
    try {
      return window.matchMedia('(max-width: 768px)').matches;
    } catch {
      return false;
    }
  }

  private openOverlay(restoreData?: any): void {
    if (this.overlayRef) {
      this.closeOverlay();
    }

    const isSmall = this.isSmallScreen();
    const positionStrategy = isSmall
      ? this.overlay.position().global().left('0').top('0')
      : this.overlay
          .position()
          .flexibleConnectedTo(this.elementRef)
          .withViewportMargin(8)
          .withPush(true)
          .withFlexibleDimensions(true)
          .withPositions([
            {
              originX: 'end',
              originY: 'top',
              overlayX: 'end',
              overlayY: 'top',
              offsetY: 5
            }
          ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      width: isSmall ? '100vw' : undefined,
      height: isSmall ? '100vh' : undefined,
      scrollStrategy: isSmall
        ? this.overlay.scrollStrategies.block()
        : this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      disposeOnNavigation: true,
      panelClass: isSmall
        ? ['inline-add-overlay-panel', 'inline-add-overlay-panel--full']
        : ['inline-add-overlay-panel']
    });

    const portal = new ComponentPortal(InlineAddPanelComponent);
    const componentRef = this.overlayRef.attach(portal);
    // Prevent events inside overlay from bubbling to underlying UI which may close it
    const paneEl = (this.overlayRef as any)?._pane as HTMLElement | undefined;
    if (paneEl) {
      const stop = (e: Event) => e.stopPropagation();
      paneEl.addEventListener('mousedown', stop, { passive: true });
      paneEl.addEventListener('click', stop, { passive: true });
      paneEl.addEventListener('mouseup', stop, { passive: true });
    }

    // Configure the inline add panel component
    const inlineAddPanel = componentRef.instance;
    this.currentInlineAddPanel = inlineAddPanel; // Store the reference
    inlineAddPanel.config = this.stockConfig;
    inlineAddPanel.isOverlay = true;
    // Prepare instant data based on search text
    // Use pendingInstantData from base class (captured synchronously) first, then fallback to storedInstantData
    const instant = (
      this.pendingInstantData ||
      this.storedInstantData ||
      this.instantData ||
      ''
    ).trim();
    const initData: any = {
      ...(this.inlineDefaults || {}),
      ...(restoreData || {})
    };
    if (instant) initData.name = instant;
    if (Object.keys(initData).length > 0) inlineAddPanel.initialData = initData;
    inlineAddPanel.visible = true;
    // Ensure lifecycle runs with inputs set
    componentRef.changeDetectorRef.detectChanges();
    // Robustly set the 'name' control once the form is ready
    const tryApplyInstantToForm = (retries: number = 20) => {
      if (!instant) return;
      const fg: any = (inlineAddPanel as any).formGroup;
      // Prefer configured instant field; fallback to 'name'
      const instantField =
        (inlineAddPanel as any).config?.fields?.find(
          (f: any) => f.isInstantData
        )?.name || 'name';
      const control = fg?.get ? fg.get(instantField) : null;
      if (control) {
        control.setValue(instant);
        control.markAsTouched();
        control.markAsDirty();
        // Notify field change to trigger any dependent logic
        try {
          (inlineAddPanel as any).onFieldChange(instantField, instant);
        } catch {}
        // Refresh view
        try {
          componentRef.changeDetectorRef.detectChanges();
        } catch {}
        // Clear pending since we've applied it (use base class pendingInstantData)
        this.pendingInstantData = '';
        return;
      }
      if (retries > 0) {
        setTimeout(() => tryApplyInstantToForm(retries - 1), 50);
      }
    };
    tryApplyInstantToForm();

    // Subscribe to events
    inlineAddPanel.itemCreated
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.onStockCreated(event);
        this.closeOverlay();
      });

    inlineAddPanel.cancelled.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onStockCancelled();
      this.closeOverlay();
    });

    inlineAddPanel.visibleChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(visible => {
        if (visible) {
          const instantNow = (
            this.storedInstantData ||
            this.instantData ||
            ''
          ).trim();
          if (instantNow) {
            try {
              inlineAddPanel.changeInstantData(instantNow);
            } catch {}
            setTimeout(() => {
              try {
                inlineAddPanel.changeInstantData(instantNow);
              } catch {}
            }, 0);
          }
        } else {
          this.closeOverlay();
        }
      });

    inlineAddPanel.moreDetails.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onStockMoreDetails();
      this.closeOverlay();
    });

    inlineAddPanel.fieldChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.onFieldChange(event);
      });

    // Removed backdrop close; rely on inline panel events to close
  }

  private closeOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.currentInlineAddPanel = null; // Clear the reference
    this.showInlineAddPanel = false;
  }

  onStockCreated(event: { item: any; selectionConfig?: any }): void {
    if (event.item && event.selectionConfig) {
      // Create the stock object in the expected format
      const stock = {
        id: event.item[event.selectionConfig.valueField],
        code: event.item.code,
        name: event.item[event.selectionConfig.labelField],
        baseUOM: event.item.baseUOM,
        accountPresetId: event.item.accountPresetId,
        salesClassification: event.item.salesClassification,
        isStockControl: event.item.isStockControl || false,
        isActive: event.item.isActive !== false,
        balance: 0
      };

      // Add the new stock to the list and select it
      this.list.unshift(stock);

      // Set the stock object in the form control to trigger stock.valueChanges
      this.value = stock.id;
      this.selected = stock.id;

      // Set the stock object directly in the form control
      this.onChange(stock);
      this.writeValue(stock);
      // Emit addNewStock event for parent component to handle
      this.addNewStock.emit(stock);

      // Clear stored instant data after successful creation
      this.clearStoredInstantData();

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  onStockCancelled(): void {
    this.showInlineAddPanel = false;
    // Clear stored instant data when user explicitly cancels
    this.clearStoredInstantData();
  }

  onStockMoreDetails(): void {
    // Navigate to stock details page with all the current form data
    const formData = this.currentInlineAddPanel?.formGroup?.value;
    if (formData) {
      // Create query params with all form fields
      const queryParams: any = {
        autoPopulate: 1
      };

      // Add all form fields to query params
      if (formData.name) queryParams.StockName = formData.name;
      if (formData.code) queryParams.StockCode = formData.code;
      if (formData.accountPresetId)
        queryParams.AccountPresetId = formData.accountPresetId;
      if (formData.categoryId) queryParams.CategoryId = formData.categoryId;
      if (formData.classId) queryParams.ClassId = formData.classId;
      if (formData.groupId) queryParams.GroupId = formData.groupId;

      queryParams.autoPopulate = 1;

      this.router.navigate(['/stk/stock/detail/0'], {
        queryParams: queryParams
      });
    } else {
      this.router.navigate(['/stk/stock/detail/0'], {
        queryParams: {
          autoPopulate: 1
        }
      });
    }
    this.showInlineAddPanel = false;
    // Clear stored instant data when navigating to more details
    this.clearStoredInstantData();
  }

  onFieldChange(event: { fieldName: string; value: any }): void {
    // Handle field changes - auto-code component will handle code generation
  }

  focus() {
    if (this.selector) {
      this.fetchData('').then(() => {
        if (this.selector) {
          this.selector.focus();
        }
      });
    }
  }
  private fetchInlineDefaults(): Promise<void> {
    // Ensure accountPreset is preselected using server 'new' defaults
    return new Promise(resolve => {
      this.http.get('api/stocks/new').subscribe({
        next: (data: any) => {
          const defaults: any = { ...(this.inlineDefaults || {}) };
          if (data?.accountPresetId)
            defaults.accountPresetId = data.accountPresetId;
          if (!defaults.accountPresetId && data?.accountPreset?.id)
            defaults.accountPresetId = data.accountPreset.id;
          this.inlineDefaults = defaults;
          resolve();
        },
        error: () => {
          // Leave as-is on error
          resolve();
        }
      });
    });
  }
}
